import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { App as CapacitorApp } from "@capacitor/app";

const ThemeContext = createContext({
  currentTheme: "light",
  changeCurrentTheme: () => {},
  brandTheme: "direct",
  isDirect: false,
  isMP: true,
});

export default function ThemeProvider({ children }) {
  const persistedTheme = localStorage.getItem("theme");
  const { user, selectedMerchant } = useSelector((state) => state.app);
  const [theme, setTheme] = useState(persistedTheme || "light");
  const [brandTheme, setBrandTheme] = useState(null);
  const [isApp, setIsApp] = useState(false);


  useEffect(() => {
    if(user?.type === "prospect") {
      setBrandTheme("direct");
      return;
    }
    if (!selectedMerchant) {
      CapacitorApp.getInfo()
        .then(() => {
          setIsApp(true);
          setBrandTheme("direct");
        })
        .catch(() => {
          const path = window.location.hostname;
          if (path.includes("direct") || path.includes("localhost")) {
            setBrandTheme("direct");
          } else {
            setBrandTheme("mp");
          }
        });
    } else {
      setBrandTheme(selectedMerchant.direct ? "direct" : "mp");
    }
  }, [selectedMerchant,  user]);

  const isDirect = brandTheme === "direct";
  const isMP = brandTheme === "mp";

  const changeCurrentTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  useEffect(() => {
    //  <body class="font-inter antialiased bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
    if (brandTheme === "direct") {
      document.body.classList.remove("dark:bg-[#1E1E1E]");
      document.body.classList.add("dark:bg-[#1B1E28]");
    } else {
      document.body.classList.remove("dark:bg-[#1B1E28]");
      document.body.classList.add("dark:bg-[#1E1E1E]");
    }
  }, [brandTheme, theme]);

  useEffect(() => {
    document.documentElement.classList.add("[&_*]:!transition-none");
    if (theme === "light") {
      document.documentElement.classList.remove("dark");
      document.documentElement.style.colorScheme = "light";
    } else {
      document.documentElement.classList.add("dark");
      document.documentElement.style.colorScheme = "dark";
    }

    const transitionTimeout = setTimeout(() => {
      document.documentElement.classList.remove("[&_*]:!transition-none");
    }, 1);

    return () => clearTimeout(transitionTimeout);
  }, [theme]);

  // Apply brand theme classes based on selectedMerchant.direct
  useEffect(() => {
    // Remove existing brand theme classes
    document.body.className = document.body.className
      .replace(/theme-\w+/g, "")
      .trim();

    // Add new brand theme class
    document.body.classList.add(`theme-${brandTheme}`);
  }, [brandTheme]);

  return (
    <ThemeContext.Provider
      value={{
        currentTheme: theme,
        changeCurrentTheme,
        brandTheme,
        isDirect,
        isMP,
        isApp,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useThemeProvider = () => useContext(ThemeContext);
